import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { Mail, Lock, AlertCircle, Loader } from 'lucide-react'

// Google icon component
const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
)

// Apple icon component
const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
  </svg>
)

function LoginPage() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [googleLoading, setGoogleLoading] = useState(false)
  const [appleLoading, setAppleLoading] = useState(false)
  const [error, setError] = useState('')

  const { signIn, signInWithGoogle, signInWithApple } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    if (!email || !password) {
      setError('Please fill in all fields')
      return
    }
    
    try {
      setError('')
      setLoading(true)
      await signIn(email, password)
      navigate('/')
    } catch (err) {
      console.error('Login error:', err)
      if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') {
        setError('Invalid email or password')
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.')
      } else {
        setError('Failed to sign in. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSignIn = async () => {
    try {
      setError('')
      setGoogleLoading(true)
      await signInWithGoogle()
      navigate('/')
    } catch (err) {
      console.error('Google sign-in error:', err)
      if (err.message === 'NO_PROFILE') {
        setError('No account found. Please register with an invitation code first.')
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.')
      } else {
        setError(err.message || 'Failed to sign in with Google. Please try again.')
      }
    } finally {
      setGoogleLoading(false)
    }
  }

  const handleAppleSignIn = async () => {
    try {
      setError('')
      setAppleLoading(true)
      await signInWithApple()
      navigate('/')
    } catch (err) {
      console.error('Apple sign-in error:', err)
      if (err.message === 'NO_PROFILE') {
        setError('No account found. Please register with an invitation code first.')
      } else if (err.code === 'auth/popup-closed-by-user') {
        setError('Sign-in was cancelled. Please try again.')
      } else {
        setError(err.message || 'Failed to sign in with Apple. Please try again.')
      }
    } finally {
      setAppleLoading(false)
    }
  }

  return (
    <form className="auth-form" onSubmit={handleSubmit}>
      <div className="auth-form-header">
        <h2>Welcome Back</h2>
        <p>Sign in to your NeighborTopia account</p>
      </div>

      {error && (
        <div className="form-error">
          <AlertCircle size={18} />
          <span>{error}</span>
        </div>
      )}

      {/* Google Sign In Button */}
      <button
        type="button"
        className="btn-google"
        onClick={handleGoogleSignIn}
        disabled={googleLoading || appleLoading || loading}
      >
        {googleLoading ? (
          <>
            <Loader size={18} className="spinner" />
            <span>Signing in...</span>
          </>
        ) : (
          <>
            <GoogleIcon />
            <span>Continue with Google</span>
          </>
        )}
      </button>

      {/* Apple Sign In Button */}
      <button
        type="button"
        className="btn-apple"
        onClick={handleAppleSignIn}
        disabled={appleLoading || googleLoading || loading}
      >
        {appleLoading ? (
          <>
            <Loader size={18} className="spinner" />
            <span>Signing in...</span>
          </>
        ) : (
          <>
            <AppleIcon />
            <span>Continue with Apple</span>
          </>
        )}
      </button>

      <div className="auth-divider-line">
        <span>or sign in with email</span>
      </div>

      <div className="form-group">
        <label htmlFor="email">Email Address</label>
        <div className="input-with-icon">
          <Mail size={18} className="icon" />
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoComplete="email"
            required
          />
        </div>
      </div>

      <div className="form-group">
        <label htmlFor="password">Password</label>
        <div className="input-with-icon">
          <Lock size={18} className="icon" />
          <input
            id="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            required
          />
        </div>
      </div>

      <button type="submit" className="btn-primary" disabled={loading}>
        {loading ? (
          <>
            <Loader size={18} className="spinner" />
            <span>Signing In...</span>
          </>
        ) : (
          <span>Sign In</span>
        )}
      </button>

      <div className="auth-links">
        <Link to="/forgot-password">Forgot your password?</Link>
        <p>
          Don't have an account? <Link to="/signup">Sign up with invitation code</Link>
        </p>
      </div>

      <style>{`
        .btn-google {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: white;
          border: 1px solid #dadce0;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          color: #3c4043;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-bottom: 0;
        }

        .btn-google:hover:not(:disabled) {
          background: #f8f9fa;
          border-color: #c6c6c6;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .btn-google:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .btn-apple {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 12px;
          width: 100%;
          padding: 12px 16px;
          background: #000000;
          border: 1px solid #000000;
          border-radius: 8px;
          font-size: 0.9375rem;
          font-weight: 500;
          color: #ffffff;
          cursor: pointer;
          transition: all 0.2s ease;
          margin-top: 10px;
          margin-bottom: 0;
        }

        .btn-apple:hover:not(:disabled) {
          background: #1a1a1a;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }

        .btn-apple:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .auth-divider-line {
          display: flex;
          align-items: center;
          text-align: center;
          margin: 20px 0;
        }

        .auth-divider-line::before,
        .auth-divider-line::after {
          content: '';
          flex: 1;
          border-bottom: 1px solid #e0e0e0;
        }

        .auth-divider-line span {
          padding: 0 16px;
          font-size: 0.8125rem;
          color: #6b7280;
          white-space: nowrap;
        }
      `}</style>
    </form>
  )
}

export default LoginPage
